import type { ViewLineMarks } from '../print/lineMarks';
import type { SendMailbox } from '../records/companyMailbox';
import type { TaxJurisdictionTotal } from '../tax/localTax';
import type { DocumentFileRow } from './salesOrder';
import type { ApprovalView } from './approvalGate';
import { cents, computeLineAmount, roundHalfUpCents, type Currency, type LineDiscount } from '../money';
import type { PrintDocument } from './printRegions';
import type { PrintSheetLayout, PrintSheetHtml } from '../print/templates';
import { approvalCleared } from './statuses';
import type { ApprovalStatusValue, ChangeOrderStatusValue, InvoiceStatusValue } from './statuses';
import type { DividerRow } from '../composites/itemTable';
import type { FrozenDealTeam } from './frozenDealTeam';
import type { UnpricedCommissionView } from '../commissions/unpriced';
import type { PayOnlineOffer } from '../accounting/portalPay';
import type { DocumentDeliveryView } from './delivery';

/**
 * The Invoice's own rules as pure functions (piece 22): the due date derived
 * once from the document's own terms, the per-line invoiced state and the
 * uninvoiced remainder Add to invoice reads, the dispute and the write-off
 * with their required facts, and the approval an automatic invoice inherits
 * from its Sales Order (decision 0192).
 */

/** How many days a free-text payment term carries; anything that names no number is due on receipt (found while building, 2026-09-15). */
export function termDays(paymentTerms: string | null | undefined): number {
  const match = /(\d+)/.exec(paymentTerms ?? '');
  if (!match) return 0;
  const days = Number(match[1]);
  return Number.isFinite(days) && days >= 0 ? days : 0;
}

/**
 * 🔨 The due date is derived once at creation from `invoiceDate` and the
 * document's own `paymentTerms`, and is never re-read from the Company
 * afterwards: renegotiating a customer's terms must not move every unpaid
 * invoice's due date.
 */
export function dueDateFrom(invoiceDate: Date, paymentTerms: string | null | undefined): Date {
  const due = new Date(invoiceDate.getTime());
  due.setUTCDate(due.getUTCDate() + termDays(paymentTerms));
  return due;
}

/**
 * 🔒 The due date an edit writes (T114; decision 0681): re-derived from the new
 * date and the document's own terms, unless the stored one is not what the
 * stored date and terms derive. 🔨 That is how a due date written by hand, or
 * by the cutover's load, is told apart with no column to say so: nothing the
 * derivation writes can differ from it, so a difference is somebody's own date
 * and is kept. The form reads it live and the save writes it, so the two agree.
 */
export function rederivedDueDate(stored: { invoiceDate: Date; paymentTerms: string | null; dueDate: Date }, invoiceDate: Date, paymentTerms: string | null): Date {
  const derivedBefore = dueDateFrom(stored.invoiceDate, stored.paymentTerms).getTime() === stored.dueDate.getTime();
  return derivedBefore ? dueDateFrom(invoiceDate, paymentTerms) : stored.dueDate;
}

export interface InvoicedLineFacts {
  /** The Sales Order line's own ordered quantity. */
  quantity: number;
  /** The sum of the quantities of the Invoice lines pointing at it. */
  invoiced: number;
}

export type InvoicedState = 'none' | 'partial' | 'full';

/** 🔨 Per-line invoiced state is derived from the Invoice lines pointing at the Sales Order line, never stored. */
export function invoicedState(line: InvoicedLineFacts): InvoicedState {
  if (line.invoiced <= 0) return 'none';
  return line.invoiced >= line.quantity ? 'full' : 'partial';
}

/** What a partial line shows, for example 3 of 4; a header that only reads "Partially invoiced" gives no way to see which lines. */
export function invoicedLabel(line: InvoicedLineFacts): string {
  const state = invoicedState(line);
  if (state === 'none') return 'not invoiced';
  if (state === 'full') return 'invoiced';
  return `${line.invoiced} of ${line.quantity}`;
}

/**
 * 🔒 An invoice line bills a change order line where that change order belongs
 * to the invoice's own Sales Order and the gate has cleared it (decision 0501).
 * A change order not yet agreed is money the customer has not said yes to, and
 * a withdrawn or rejected one is money nobody will ever say yes to.
 */
export function billableChangeOrderProblem(facts: { changeOrderNumber: string; salesOrderId: string; invoiceSalesOrderId: string | null; status: ChangeOrderStatusValue; approvalStatus: ApprovalStatusValue }): string | null {
  if (facts.invoiceSalesOrderId === null || facts.salesOrderId !== facts.invoiceSalesOrderId) return `${facts.changeOrderNumber} belongs to another Sales Order, so this invoice does not bill its lines`;
  if (facts.status === 'Withdrawn' || facts.status === 'Rejected') return `${facts.changeOrderNumber} is ${facts.status}, so its lines are not billed`;
  if (!approvalCleared(facts.approvalStatus)) return `${facts.changeOrderNumber} is ${facts.approvalStatus}, so the customer has not agreed its lines yet`;
  return null;
}

/** 🔨 Add to invoice adds only the uninvoiced remainder of a line, never the whole line where part is already billed. */
export function uninvoicedRemainder(line: InvoicedLineFacts): number {
  return Math.max(0, line.quantity - line.invoiced);
}

/** 🔨 `Disputed` is set by a person who states a reason, and nothing sets it or clears it automatically (decision 0197). */
export function disputeProblem(status: InvoiceStatusValue, reason: string | null | undefined): string | null {
  if (status === 'Draft') return 'a Draft invoice is edited rather than disputed';
  if (status === 'Void') return 'a void invoice is not disputed';
  if (!reason?.trim()) return 'a dispute names the reason the customer gave; type what the customer said is wrong';
  return null;
}

/** 🔨 Write Off takes a reason and an authoriser, and a written-off invoice is never voided — the sale happened (decision 0197). */
export function writeOffProblem(status: InvoiceStatusValue, reason: string | null | undefined): string | null {
  if (status !== 'Sent' && status !== 'Disputed') return `an invoice at ${status} is not written off; only a sent or disputed invoice is`;
  if (!reason?.trim()) return 'a write-off names its reason, so the write-offs report can list them by reason';
  return null;
}

/**
 * 🔨 An Invoice issues only against a resolved travel figure: a Work Order
 * whose lookup failed still saves and still dispatches, and the invoice is
 * what refuses. The message is the shared travel module's.
 */
export function invoiceIssueProblem(facts: { status: InvoiceStatusValue; hasLines: boolean; travelProblem: string | null; approvalStatus: ApprovalStatusValue; worseThanOrder?: string | null }): string | null {
  if (facts.status !== 'Draft') return `invoice ${facts.status} has already been issued`;
  if (!facts.hasLines) return 'an invoice with no lines is not sent';
  // 🔨 The cleared test lives in `approvalCleared` alone (decision 0411); a second copy of it here is how the two drift.
  if (!approvalCleared(facts.approvalStatus)) return `the approval gate holds this invoice: it is ${facts.approvalStatus}`;
  // 🔨 A worse line is its own fact and its own sentence (decision 0503); it was an invented Pending on a document that carries no approval.
  if (facts.worseThanOrder) return facts.worseThanOrder;
  return facts.travelProblem;
}

/**
 * 🔒 The refusal decision 0503 puts in place of decision 0192's invented
 * Pending: an Invoice whose lines are worse for the Organization than the lines
 * they bill does not issue, and the sentence names the order. The Invoice gains
 * no approval axis, because the thing to be approved is the order.
 */
export function worseThanOrderProblem(orderNumber: string, lines: readonly ComparedLine[]): string | null {
  const worse = lines.filter((line) => line.invoiceUnitPriceCents < line.orderUnitPriceCents || line.invoiceDiscountPercent > line.orderDiscountPercent).length;
  if (worse === 0) return null;
  const many = worse === 1 ? 'a line' : `${worse} lines`;
  return `${many} of this invoice bills below what ${orderNumber} agreed, at a lower price or a larger discount; change the order or bill it at the order's figures`;
}

export interface ComparedLine {
  /** The Sales Order line's price, discount and margin, and the Invoice line's own. */
  orderUnitPriceCents: number;
  invoiceUnitPriceCents: number;
  orderDiscountPercent: number;
  invoiceDiscountPercent: number;
}

/**
 * An automatically created Invoice inherits its Sales Order's approval when
 * every line's unit price and discount are unchanged or better for the
 * Organization; a lower price or a higher discount reverts it to Pending
 * (decision 0192).
 */
export function inheritsApproval(orderApproval: ApprovalStatusValue, lines: readonly ComparedLine[]): boolean {
  if (!approvalCleared(orderApproval)) return false;
  return lines.every((line) => line.invoiceUnitPriceCents >= line.orderUnitPriceCents && line.invoiceDiscountPercent <= line.orderDiscountPercent);
}

/** The finance-charge rows already exist; a row carrying the pair never accrues a charge of its own (decision 0397). */
export function accruesFinanceCharge(invoice: { financeChargeSourceInvoiceId: string | null }): boolean {
  return invoice.financeChargeSourceInvoiceId === null;
}

export interface InvoiceTotalLine {
  quantity: number;
  unitPriceCents: number | null;
  discount?: LineDiscount | null;
  taxAmountCents?: number | null;
}

/** The invoice's total: each line rounded once with its discount inside, plus the tax the lines carry. */
export function invoiceTotal(lines: readonly InvoiceTotalLine[], currencyCode = 'USD'): { netCents: number; taxCents: number; total: Currency } {
  let net = 0;
  let tax = 0;
  for (const line of lines) {
    if (line.unitPriceCents === null || line.unitPriceCents === undefined) continue;
    net += computeLineAmount(line.quantity, cents(line.unitPriceCents, currencyCode), line.discount ?? undefined).amount;
    tax += line.taxAmountCents ?? 0;
  }
  return { netCents: net, taxCents: tax, total: cents(net + tax, currencyCode) };
}

export type InvoicePrimaryAction = 'issue' | 'clearDispute' | null;

export interface InvoiceFooter {
  primary: InvoicePrimaryAction;
  /** A dispute is a person's word against a sent invoice, and never against a Draft or a void one (decision 0197). */
  mayDispute: boolean;
  /** 🔨 Write Off takes a sent or a disputed invoice; a written-off one is never voided, because the sale happened. */
  mayWriteOff: boolean;
  mayVoid: boolean;
  /** 🔨 Edit reopens a Draft alone; an issued invoice is the copy the customer's accounts-payable system keys on. */
  mayEdit: boolean;
  /**
   * 🔒 Record a payment against this invoice, offered where one is owed
   * (piece 22, the payment's screens). The act opens a new Payment on that
   * invoice; it is never a second payment form of the Invoice's own
   * (decision 0409).
   */
  mayRecordPayment: boolean;
  /** 🔒 Raise a credit note against this invoice, offered on an issued one and never on a Draft or a void one (T921; decision 1704); absent from an older reply. */
  mayRaiseCreditNote?: boolean;
  /** 🔒 Undo Write-Off, offered on a written-off invoice alone (decision 8860); absent from an older reply. */
  mayUndoWriteOff?: boolean;
  /** 🔒 Send again on an issued invoice short of Void (decision 2630): the issued copy is mailed, with no second number or posting. Absent from an older reply. */
  mayResend?: boolean;
  /** The reason the primary is held, where the gate or a missing line holds it. */
  refusal: string | null;
}

/**
 * The Invoice footer's one primary action per status (piece 22, the Quote's
 * shape). The displayed status is what the footer reads, because a derived
 * member is what the person is looking at; the stored member is what the
 * server writes.
 */
export function invoiceFooter(facts: { status: InvoiceStatusValue; storedStatus: InvoiceStatusValue; hasLines: boolean; approvalStatus: ApprovalStatusValue; travelProblem?: string | null; worseThanOrder?: string | null; openBalanceCents?: number }): InvoiceFooter {
  const stored = facts.storedStatus;
  const draft = stored === 'Draft';
  const gone = stored === 'Void' || stored === 'WrittenOff';
  const problem = draft ? invoiceIssueProblem({ status: stored, hasLines: facts.hasLines, travelProblem: facts.travelProblem ?? null, approvalStatus: facts.approvalStatus, worseThanOrder: facts.worseThanOrder ?? null }) : null;
  return {
    primary: draft ? 'issue' : stored === 'Disputed' ? 'clearDispute' : null,
    mayDispute: !draft && !gone && facts.status !== 'Paid' && stored !== 'Disputed',
    // 🔒 Decision 3264, amended: a Paid invoice, a zero one included, owes nothing, so Write Off is not offered on it.
    mayWriteOff: (stored === 'Sent' || stored === 'Disputed') && facts.status !== 'Paid',
    mayUndoWriteOff: stored === 'WrittenOff',
    // 🔨 A written-off invoice is never voided — the sale happened (decision 0197).
    mayVoid: !gone,
    mayEdit: draft,
    /**
     * 🔨 The same three statuses the payment's own screen offers an invoice at,
     * and only while something is still owed: an unnumbered Draft nobody owes
     * yet, and a voided or fully paid invoice, are not offered.
     * 🔨 A written-off invoice IS offered, because a payment arriving against
     * one reverses the write-off and then allocates (decision 0197).
     */
    mayRecordPayment: (stored === 'Sent' || stored === 'Disputed' || stored === 'WrittenOff') && (facts.openBalanceCents ?? 0) > 0,
    mayRaiseCreditNote: invoiceTakesCreditNote(stored),
    mayResend: !draft && stored !== 'Void',
    refusal: problem,
  };
}

/**
 * 🔒 The invoices a credit note is raised against: every issued one, and
 * never a Draft, which nobody owes yet, or a void one (T921; decision 1704).
 * The Invoice's footer and the credit note form's picker both read this.
 */
export const INVOICE_STATUSES_NOT_CREDITED = ['Draft', 'Void'] as const;

export function invoiceTakesCreditNote(stored: InvoiceStatusValue): boolean {
  return !(INVOICE_STATUSES_NOT_CREDITED as readonly string[]).includes(stored);
}

/** What earlier credit notes have already taken off one invoice line: the quantity and the tax that went with it. */
export interface CreditedSoFar {
  quantity: number;
  taxCents: number;
}

/**
 * The share of a line's tax a part-quantity credit carries.
 *
 * 🔨 A sum of roundings is not the rounding of a sum, so each note takes the
 * rounding of the *cumulative* credit less what earlier notes already took,
 * never its own independent rounding (found in review, 2026-09-15). The last
 * note over a line therefore closes the line's tax exactly: splitting a 5c
 * line across two notes credited 6c before this, and a 10c line across three
 * credited 9c, permanently and with nothing reporting it.
 */
export function taxShare(lineTaxCents: number, creditedQuantity: number, lineQuantity: number, alreadyCredited: CreditedSoFar = { quantity: 0, taxCents: 0 }): number {
  if (lineQuantity <= 0) return 0;
  const through = Math.min(lineQuantity, alreadyCredited.quantity + creditedQuantity);
  const cumulative = roundHalfUpCents((lineTaxCents * through) / lineQuantity);
  // 🔨 Never negative, and never past what the line still has to give.
  return Math.max(0, Math.min(cumulative - alreadyCredited.taxCents, lineTaxCents - alreadyCredited.taxCents));
}

/**
 * 🔒 The Invoice as its own routes answer it, declared once so the service that
 * builds it and the screen that reads it are the same shape (piece 22;
 * decision 0409). 💰 Each page hand-copied its own private copy of this, so
 * renaming a field on the server left both sides compiling and the screen
 * reading `undefined` (found in review, 2026-09-15).
 */
export interface InvoiceLineRow extends ViewLineMarks {
  id: string;
  lineNumber: number;
  itemId: string | null;
  /** The Item's own name, which the edit form reopens on and the sheet's Item cell leads with (T118; decision 0640). */
  itemName: string | null;
  sku: string | null;
  description: string;
  quantity: number;
  unitName: string;
  unitPriceCents: number | null;
  taxAmountCents: number;
  /** 🔨 The line's own tax control (decisions 0086, 0435); the amount above is the calculation's and is never typed. */
  taxCategoryId: string;
  taxCategoryName: string;
  /** 🔨 The account the line posts to, resolved once at creation with the line's own override first (decisions 0097, 0435). */
  accountId: string;
  accountLabel: string;
  amountCents: number;
  sourceLineId: string | null;
  /** What the edit form sends back unchanged, so a rewrite of the line list keeps the discount it opened on. */
  discountType: string | null;
  discountValue: number | null;
}

export interface InvoiceAnswer {
  /** 🔒 The Account's mailbox the send window opens To on, chosen by the server for the Organization's day and never sent to an outside login (decision 2974); null where the Account holds none; absent from an older reply. */
  sendMailbox?: SendMailbox | null;
  /** 🔒 Where the newest mailed send stands, which the page's Delivered panel draws (decisions 1226, 2630); null where nothing was mailed, and never sent to an outside login. Absent from an older reply. */
  delivery?: DocumentDeliveryView | null;
  /** 🔒 Pay this invoice, answered to a portal login of the invoice's company where something is owed and a rail is reachable (decision 1650); null or absent otherwise. */
  payOnline?: PayOnlineOffer | null;
  /** 🔒 Why the document rests where it does, and a Draft's one primary action (T131, T223; decision 0713); absent from an older reply. */
  approval?: ApprovalView;
  /** 🔒 The group dividers among the lines, each at its place in the one reading order (decision 0650); absent from an older reply. */
  dividers?: DividerRow[];
  id: string;
  invoiceNumber: string | null;
  companyId: string;
  companyName: string;
  /** The stored member; PartiallyPaid, Overdue and Paid derive over it and are never written. */
  storedStatus: InvoiceStatusValue;
  /** 🔒 The date and the threshold the issue window warns on, on a Draft awaiting its first issue (decision 1227); absent from an older reply. */
  staleSend?: { documentDate: string; thresholdDays: number } | null;
  status: InvoiceStatusValue;
  disputeReason: string | null;
  writeOffReason: string | null;
  writtenOffAt: string | null;
  /** 🔒 True on an invoice the import loaded as open at the cut-off (decisions 2990, 2992); the page marks it Opening. Absent from an older reply. */
  isOpening?: boolean;
  sourceSalesOrderId: string | null;
  sourceSalesOrderNumber: string | null;
  /** 🔒 The Work Order this invoice billed, drawn as a link on its page (decision 0502); null on an invoice that billed no job. */
  workOrderId: string | null;
  workOrderNumber: string | null;
  /** 🔒 What is billed against the customer's PO past its amount, this invoice included (decision 2594); a warning, never a refusal. Absent from an older reply. */
  customerPoWarning?: string | null;
  invoiceDate: string;
  dueDate: string;
  paymentTerms: string;
  isFinal: boolean;
  currencyCode: string;
  netCents: number;
  taxCents: number;
  /** 🔒 One row per jurisdiction the document stored beside its lines (decision 0738); empty on one saved before the rows existed. */
  taxRows?: TaxJurisdictionTotal[];
  totalCents: number;
  amountPaidCents: number;
  openBalanceCents: number;
  lines: InvoiceLineRow[];
  /** 🔒 The one projection the page's view mode and the PDF both render, built on the server and never in the browser (piece 27). */
  printDocument: PrintDocument;
  /**
   * 🔒 The layout the document prints on, resolved once beside the projection
   * (decision 0567): view mode positions these region rows, so a region nudged
   * in the editor moves on the page. Absent where no template is laid out, and
   * the page then falls back to the reading order it drew before.
   */
  printLayout?: PrintSheetLayout | null;
  /** 🔒 The sheet both views read, as the printer's own HTML, in this same answer (T169). */
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687). */
  writeRefusal?: string | null;
  /** 🔒 The Deal's team as it stood when this document was created (decision 0765); null or absent where it carries no Deal. */
  dealTeam?: FrozenDealTeam | null;
  /** The one primary action the displayed status names, with what holds it (piece 22). */
  footer: InvoiceFooter;
  /** 🔒 The files attached to this invoice, drawn on its own page (decision 0457). */
  files: DocumentFileRow[];
  /**
   * 🔒 The warning an internal reader sees while the invoice's commission waits
   * for a rate (decision 1221); null once priced or voided, and absent from an
   * outside login's answer and an older reply.
   */
  unpricedCommission?: UnpricedCommissionView | null;
}
